import type { EngineDocument } from "../scene/model";
import type { ServerTemplate } from "./native-template-record";
import "./native-templates.css";
interface Template {
  id: string;
  title: string;
  description: string;
  category: string;
  width: number;
  height: number;
  server?: ServerTemplate;
}
interface TemplateHost {
  request(action: () => void): void;
  prepare(): (() => boolean) | null;
  read(project: unknown): Promise<EngineDocument>;
  occupied(): boolean;
  save(): Promise<boolean>;
  commit(document: EngineDocument): void;
  message(text: string, error?: boolean): void;
  api(
    path: string,
  ): Promise<{ templates?: ServerTemplate[]; template?: ServerTemplate }>;
  sharedOpened(template: ServerTemplate): void;
}
const base =
  "/templates/boxed/controllers/creatorenginepro/assets/starter-templates-v1/";
const root = () => document.getElementById("native-editor")!;
const query = <T extends HTMLElement>(element: Element, selector: string) =>
  element.querySelector<T>(selector)!;
/** Original gallery markup, assets and interaction, with a native document port. */
export class NativeTemplates {
  private section = document.createElement("section");
  private catalog: Template[] = [];
  private busy = false;
  private grid: HTMLElement;
  private status: HTMLElement;
  private button: HTMLButtonElement;
  constructor(private host: TemplateHost) {
    this.section.className = "starter-templates";
    this.section.setAttribute("aria-labelledby", "starter-templates-title");
    this.section.innerHTML =
      '<h3 id="starter-templates-title">Начните с шаблона</h3><p>Готовые макеты: измените тексты, цвета и картинки.</p><div class="starter-template-grid"></div><button type="button" class="starter-catalog-open">Открыть весь каталог</button><p class="starter-template-status" role="status" aria-live="polite"></p>';
    document.getElementById("native-page-properties")!.append(this.section);
    this.grid = query(this.section, ".starter-template-grid");
    this.status = query(this.section, ".starter-template-status");
    this.button = query(this.section, ".starter-catalog-open");
    this.button.disabled = true;
    this.button.onclick = () => this.host.request(() => this.showCatalog());
    void this.load();
  }
  private image(t: Template) {
    const img = document.createElement("img");
    if (t.server && !t.server.preview_url) {
      img.hidden = true;
      return img;
    }
    img.src = t.server?.preview_url || base + t.id + ".png?v=20260917-layout2";
    img.alt = "";
    img.loading = "lazy";
    return img;
  }
  private async load() {
    try {
      const response = await fetch(base + "catalog.json.txt", {
        cache: "no-cache",
        signal: AbortSignal.timeout(20000),
      });
      if (!response.ok) throw new Error();
      const catalog: unknown = await response.json();
      if (
        !Array.isArray(catalog) ||
        !catalog.length ||
        catalog.some(
          (t) =>
            !t ||
            !/^[a-z0-9-]+$/.test(t.id) ||
            [t.title, t.category, t.description].some(
              (v) => typeof v !== "string",
            ) ||
            !Number.isFinite(t.width) ||
            !Number.isFinite(t.height),
        )
      )
        throw new Error();
      this.catalog = catalog;
      this.grid.replaceChildren();
      for (const t of this.catalog.slice(0, 4)) {
        const card = document.createElement("button");
        card.type = "button";
        card.className = "starter-template-card";
        card.title = t.description;
        card.setAttribute("aria-label", `Открыть шаблон «${t.title}»`);
        const img = this.image(t);
        img.width = 240;
        img.height = 170;
        const category = document.createElement("small"),
          title = document.createElement("strong"),
          size = document.createElement("span");
        category.textContent = t.category;
        title.textContent = t.title;
        size.textContent = t.width + " × " + t.height;
        card.append(img, category, title, size);
        card.onclick = () => this.host.request(() => void this.open(t));
        this.grid.append(card);
      }
      this.button.disabled = false;
      this.status.textContent = "";
      await this.refreshShared();
    } catch {
      this.status.textContent = "Не удалось загрузить шаблоны. ";
      const retry = document.createElement("button");
      retry.type = "button";
      retry.textContent = "Повторить";
      retry.onclick = () => void this.load();
      this.status.append(retry);
    }
  }
  async refreshShared() {
    try {
      const result = await this.host.api("/templates");
      this.catalog = [
        ...this.catalog.filter((t) => !t.server),
        ...(result.templates || []).map((server) => ({
          id: "shared-" + server.id,
          title: server.title,
          description: server.description,
          category: "Общие шаблоны",
          width: 0,
          height: 0,
          server,
        })),
      ];
    } catch {
      /* Starter templates remain available when the shared catalog is offline. */
    }
  }
  private showCatalog() {
    if (this.busy || !this.catalog.length) return;
    const modal = document.createElement("dialog");
    modal.className = "starter-catalog";
    modal.setAttribute("aria-labelledby", "starter-catalog-title");
    modal.innerHTML =
      '<header><div><h2 id="starter-catalog-title">Каталог шаблонов</h2><p>Выберите макет слева, чтобы рассмотреть его перед открытием.</p></div><button type="button" class="catalog-close" aria-label="Закрыть каталог">×</button></header><div class="catalog-body"><div class="catalog-list"><input type="search" aria-label="Поиск шаблонов" placeholder="Найти шаблон…"><div class="catalog-cards"></div><p class="catalog-empty" hidden>Ничего не найдено. Попробуйте другое слово.</p></div><section class="catalog-preview" aria-label="Предпросмотр шаблона"><img alt=""><h3></h3><p></p></section></div><footer><span>Все элементы остаются редактируемыми</span><button type="button" class="catalog-cancel">Отмена</button><button type="button" class="catalog-use">Использовать шаблон</button></footer>';
    let selected = this.catalog[0]!;
    const list = query(modal, ".catalog-cards"),
      preview = query(modal, ".catalog-preview");
    const select = (t: Template) => {
      selected = t;
      const img = query<HTMLImageElement>(preview, "img");
      const source = this.image(t);
      img.hidden = source.hidden;
      if (source.src) img.src = source.src;
      else img.removeAttribute("src");
      img.alt = t.title;
      query(preview, "h3").textContent = t.title;
      query(preview, "p").textContent =
        t.description + (t.width ? " · " + t.width + " × " + t.height : "");
      list
        .querySelectorAll("button")
        .forEach((b) =>
          b.setAttribute("aria-pressed", String(b.dataset.id === t.id)),
        );
    };
    for (const t of this.catalog) {
      const card = document.createElement("button");
      card.type = "button";
      card.className = "starter-template-card";
      card.dataset.id = t.id;
      card.dataset.search = (
        t.title +
        " " +
        t.category +
        " " +
        t.description +
        " " +
        (t.server?.tags.join(" ") || "")
      ).toLowerCase();
      const label = document.createElement("strong");
      label.textContent = t.title;
      card.append(this.image(t), label);
      card.onclick = () => select(t);
      list.append(card);
    }
    const input = query<HTMLInputElement>(modal, "input");
    input.oninput = () => {
      let count = 0;
      list.querySelectorAll("button").forEach((b) => {
        b.hidden = !b.dataset.search!.includes(
          input.value.trim().toLowerCase(),
        );
        if (!b.hidden) count++;
      });
      query(modal, ".catalog-empty").hidden = !!count;
    };
    modal
      .querySelectorAll<HTMLButtonElement>(".catalog-close,.catalog-cancel")
      .forEach((b) => (b.onclick = () => modal.close()));
    query<HTMLButtonElement>(modal, ".catalog-use").onclick = () =>
      modal.close("use");
    modal.addEventListener(
      "close",
      () => {
        modal.remove();
        this.button.focus();
        if (modal.returnValue === "use") void this.open(selected);
      },
      { once: true },
    );
    root().append(modal);
    select(selected);
    modal.showModal();
  }
  private replacement(title: string) {
    return new Promise<string>((resolve) => {
      const dialog = document.createElement("dialog");
      dialog.className = "starter-template-dialog";
      const heading = document.createElement("h2"),
        info = document.createElement("p"),
        actions = document.createElement("div");
      heading.id = "starter-template-confirm-title";
      heading.textContent = `Открыть «${title}»?`;
      dialog.setAttribute("aria-labelledby", heading.id);
      info.textContent =
        "Шаблон заменит текущий холст. Сохраните работу в файл, чтобы вернуться к ней позже.";
      for (const [value, label] of [
        ["save", "Сохранить и открыть"],
        ["replace", "Открыть без сохранения"],
        ["cancel", "Отмена"],
      ]) {
        const b = document.createElement("button");
        b.type = "button";
        b.textContent = label!;
        b.dataset.action = value;
        b.onclick = () => dialog.close(value);
        actions.append(b);
      }
      dialog.append(heading, info, actions);
      root().append(dialog);
      dialog.addEventListener(
        "close",
        () => {
          dialog.remove();
          resolve(dialog.returnValue);
        },
        { once: true },
      );
      dialog.showModal();
      (actions.lastElementChild as HTMLElement).focus();
    });
  }
  private async open(t: Template) {
    if (this.busy) return;
    const current = this.host.prepare();
    if (!current) return;
    this.busy = true;
    this.button.disabled = true;
    this.grid.querySelectorAll("button").forEach((b) => (b.disabled = true));
    this.status.textContent = "Загружаем шаблон…";
    const check = () => {
      if (!current())
        throw new Error(
          "За время загрузки началось другое действие. Откройте шаблон ещё раз.",
        );
    };
    try {
      const response = t.server
        ? null
        : await fetch(base + t.id + ".json.txt", {
            cache: "no-cache",
            signal: AbortSignal.timeout(20000),
          });
      if (response && !response.ok)
        throw new Error("Не удалось загрузить макет. Попробуйте ещё раз.");
      const record = t.server
        ? (await this.host.api("/templates?id=" + t.server.id)).template
        : undefined;
      const scene = await this.host.read(
        record ? record.data : await response!.json(),
      );
      check();
      if (this.host.occupied()) {
        const answer = await this.replacement(t.title);
        check();
        if (answer !== "save" && answer !== "replace") {
          this.status.textContent = "";
          return;
        }
        if (answer === "save") {
          if (!(await this.host.save()))
            throw new Error("Работа не сохранена. Шаблон не открыт.");
          check();
        }
      }
      this.host.commit(scene);
      if (record) this.host.sharedOpened(record);
      this.status.textContent = `«${t.title}» открыт. Дважды нажмите на текст, чтобы изменить его.`;
      this.host.message("Шаблон открыт — все элементы можно редактировать.");
    } catch (error) {
      this.status.textContent = (error as Error).message;
      this.host.message((error as Error).message, true);
    } finally {
      this.busy = false;
      this.button.disabled = false;
      this.grid.querySelectorAll("button").forEach((b) => (b.disabled = false));
    }
  }
}
